import { Box, Loader, Popover, Select } from '@mantine/core';
import { Label } from 'components/CreateOrderForm';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import styles from './OrderTemplates.module.scss';
import { useGetOrderTemplatesFiltersQuery } from 'redux/Api/ordersApi/ordersApi';
import { useDispatch, useSelector } from 'react-redux';
import { resetOrderInfo, setInfoOrderDetail } from 'redux/ordersDetails/slice';
import { InputSearch } from 'ui/InputSearch';
import { RootState } from 'redux/store';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import type { Template } from 'redux/Api/ordersApi/type';
import { ReactComponent as ArrowSvg } from 'assets/img/filter/arrow.svg';
import { ReactComponent as IconCloseGray } from './img/iconCloseGray.svg';
import moment from 'moment';
import { formatDateTimeForOrderApi } from 'utils/date';
import { getBrowserTimeZone, nowInTimeZone } from 'constants/timezones';

export const OrdersTemplates = () => {
  const dispatch = useDispatch();
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const handleFieldChange = (key: string, value: unknown) => {
    dispatch(setInfoOrderDetail({ key, value }));
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  const [initialTemplates, setInitialTemplates] = useState<Template[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const [popoverPage, setPopoverPage] = useState(1);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [pinnedTemplate, setPinnedTemplate] = useState<Template | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);
  const suppressSearchRef = useRef(false);
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const popoverApiPage =
    prevDebouncedSearchRef.current !== debouncedSearch ? 1 : popoverPage;

  if (prevDebouncedSearchRef.current !== debouncedSearch) {
    prevDebouncedSearchRef.current = debouncedSearch;
  }

  useEffect(() => {
    setPopoverPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!infoOrderDetail.order_preset_id) {
      setPinnedTemplate(null);
    }
  }, [infoOrderDetail.order_preset_id]);

  const {
    data: templatesData,
    isLoading,
    isFetching: isFetchingTemplates,
  } = useGetOrderTemplatesFiltersQuery({
    page: popoverApiPage,
    per_page: 20,
    ...(popoverOpened && debouncedSearch ? { search: debouncedSearch } : {}),
  });

  useEffect(() => {
    const data = templatesData?.data;
    if (data?.length && !search) {
      setInitialTemplates(data);
    }
  }, [templatesData, search]);

  const allTemplates = templatesData?.data || [];
  const hasNextPage =
    !!templatesData?.meta &&
    templatesData.meta.current_page < templatesData.meta.last_page;

  const loadMoreTemplates = useCallback(() => {
    if (isFetchingTemplates || !hasNextPage || loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;
    setPopoverPage((prev) => prev + 1);
  }, [isFetchingTemplates, hasNextPage]);

  useEffect(() => {
    if (!isFetchingTemplates) {
      loadMoreLockRef.current = false;
    }
  }, [isFetchingTemplates]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    const gapToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gapToBottom < 72 && el.scrollTop > 12) {
      loadMoreTemplates();
    }
  }, [loadMoreTemplates]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPopoverPage(1);
  };

  const handleSelectDropdownOpen = useCallback(() => {
    setPopoverOpened(true);
    suppressSearchRef.current = true;
    setSearch('');
  }, []);

  const handleSelectDropdownClose = useCallback(() => {
    suppressSearchRef.current = true;
    setPopoverOpened(false);
    setSearch('');
  }, []);

  const findTemplateById = useCallback(
    (id: string | null): Template | undefined => {
      if (!id) return undefined;
      return allTemplates.find((t) => t.id.toString() === id);
    },
    [allTemplates],
  );

  const handleSelectSearchChange = useCallback(
    (value: string) => {
      const selectedLabel = infoOrderDetail.order_preset_id
        ? findTemplateById(String(infoOrderDetail.order_preset_id))?.name?.trim() ?? ''
        : '';

      if (!popoverOpened) return;

      if (suppressSearchRef.current) {
        suppressSearchRef.current = false;
        if (!value.trim() || value === selectedLabel) return;
      }

      setSearch(value);
      setPopoverPage(1);
    },
    [popoverOpened, infoOrderDetail.order_preset_id, findTemplateById],
  );

  const applyDefaultDates = useCallback((timezone?: string) => {
    const startMoment = nowInTimeZone(timezone).add(180, 'minutes');
    const startTimeValue = startMoment.format('HH:mm');
    handleFieldChange('with_date_start', true);
    handleFieldChange('with_date_end', true);
    handleFieldChange(
      'date_start',
      formatDateTimeForOrderApi(startMoment.format('YYYY-MM-DD'), startTimeValue),
    );
    handleFieldChange(
      'date_end',
      formatDateTimeForOrderApi(startMoment.format('YYYY-MM-DD'), '23:00'),
    );
  }, []);

  const handleClearTemplate = useCallback(
    (event?: React.SyntheticEvent) => {
      event?.preventDefault();
      event?.stopPropagation();
      setPinnedTemplate(null);
      dispatch(resetOrderInfo());
      applyDefaultDates(getBrowserTimeZone());
      setSearch('');
      suppressSearchRef.current = true;
    },
    [dispatch, applyDefaultDates],
  );

  const handleSelectTemplate = (id: string | null) => {
    const selectedTemplate = findTemplateById(id);

    if (selectedTemplate) {
      setPinnedTemplate(selectedTemplate);
      handleFieldChange('order_type_id', selectedTemplate.order_type_id);
      handleFieldChange('order_type', selectedTemplate.order_type);
      handleFieldChange('unit_price', selectedTemplate.unit_price);
      handleFieldChange('unit_volume', selectedTemplate.unit_volume);
      handleFieldChange('unit_volume_min', selectedTemplate.unit_volume_min || 1);
      handleFieldChange('unit_volume_max', selectedTemplate.unit_volume_max);
      handleFieldChange('unit_measure', selectedTemplate.unit_measure);
      handleFieldChange('is_unit_based', selectedTemplate.is_unit_based ? 1 : 0);

      handleFieldChange('description', selectedTemplate.description);
      handleFieldChange(
        'accept_automatically',
        selectedTemplate.accept_automatically ? 1 : 0,
      );
      handleFieldChange('use_fast_withdraw', selectedTemplate.use_fast_withdraw ? 1 : 0);
      handleFieldChange('city', selectedTemplate.city || '');
      handleFieldChange('street', selectedTemplate.street || '');
      handleFieldChange('home', selectedTemplate.home || '');
      handleFieldChange('apartment', selectedTemplate.apartment || '');
      handleFieldChange('order_preset_id', selectedTemplate.id);
      handleFieldChange('add_tax_amount', selectedTemplate.add_tax_amount);

      if (selectedTemplate.date_start) {
        handleFieldChange('with_date_start', true);
        handleFieldChange('date_start', selectedTemplate.date_start);
      } else {
        const startMoment = nowInTimeZone(infoOrderDetail.timezone).add(180, 'minutes');
        const startTimeValue = startMoment.format('HH:mm');
        handleFieldChange('with_date_start', true);
        handleFieldChange(
          'date_start',
          formatDateTimeForOrderApi(startMoment.format('YYYY-MM-DD'), startTimeValue),
        );
      }

      if (selectedTemplate.date_end) {
        handleFieldChange('with_date_end', true);
        handleFieldChange('date_end', selectedTemplate.date_end);
      } else {
        const startDate = selectedTemplate.date_start
          ? moment(selectedTemplate.date_start).format('YYYY-MM-DD')
          : nowInTimeZone(infoOrderDetail.timezone)
              .add(180, 'minutes')
              .format('YYYY-MM-DD');
        handleFieldChange('with_date_end', true);
        handleFieldChange('date_end', formatDateTimeForOrderApi(startDate, '23:00'));
      }

      if (selectedTemplate.is_remote) {
        handleFieldChange('is_remote', 'Удаленно');
        handleFieldChange('with_address', false);
        handleFieldChange('apartment', '');
        handleFieldChange('city', '');
        handleFieldChange('street', '');
        handleFieldChange('home', '');
      } else {
        handleFieldChange('with_address', true);
        handleFieldChange('is_remote', 'По адресу');
        handleFieldChange('apartment', selectedTemplate.apartment || '');
        handleFieldChange('city', selectedTemplate.city || '');
        handleFieldChange('street', selectedTemplate.street || '');
        handleFieldChange('home', selectedTemplate.home || '');
      }
    } else {
      handleClearTemplate();
    }
  };

  const selectedFromLists = infoOrderDetail.order_preset_id
    ? findTemplateById(String(infoOrderDetail.order_preset_id))
    : undefined;
  const selectedTemplate =
    selectedFromLists ||
    (pinnedTemplate?.id === infoOrderDetail.order_preset_id ? pinnedTemplate : undefined);

  const firstFiveTemplates = initialTemplates.slice(0, 5);
  const remainingTemplates = initialTemplates.slice(5);

  const selectData = useMemo(() => {
    const items = allTemplates.map((t) => ({
      value: t.id.toString(),
      label: t.name ?? '',
    }));
    const isSearchActive = search.trim().length > 0;
    const selectedId = infoOrderDetail.order_preset_id?.toString();
    if (
      !isSearchActive &&
      selectedId &&
      selectedTemplate?.name &&
      !items.some((item) => item.value === selectedId)
    ) {
      items.unshift({
        value: selectedId,
        label: selectedTemplate.name ?? '',
      });
    }
    return items;
  }, [allTemplates, infoOrderDetail.order_preset_id, selectedTemplate, search]);

  const hasDataRef = useRef(false);

  useEffect(() => {
    if (templatesData?.data?.length && templatesData?.data?.length > 0) {
      hasDataRef.current = true;
    }
  }, [templatesData]);

  if (isLoading) return null;

  if (
    templatesData?.data?.length === 0 &&
    !hasDataRef.current &&
    !popoverOpened &&
    !search
  ) {
    return null;
  }

  return (
    <div>
      <Label text='Шаблон' />
      {isMobile ? (
        <Select
          data={selectData}
          placeholder='Выбери шаблон'
          searchable
          searchValue={popoverOpened ? search : undefined}
          onSearchChange={handleSelectSearchChange}
          onDropdownOpen={handleSelectDropdownOpen}
          onDropdownClose={handleSelectDropdownClose}
          filter={({ options }) => options}
          nothingFoundMessage='Ничего не найдено'
          scrollAreaProps={{
            viewportRef: scrollRef,
            onScrollPositionChange: handleScroll,
          }}
          styles={{
            input: {
              height: '48px',
              fontSize: '16px',
              border: 'none',
            },
            option: {
              minHeight: '48px',
              height: 'auto',
              fontSize: '16px',
              padding: '12px 16px',
              lineHeight: '1.4',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
          }}
          value={
            infoOrderDetail.order_preset_id
              ? String(infoOrderDetail.order_preset_id)
              : null
          }
          onChange={(val) => {
            if (!val) return;
            handleSelectTemplate(val);
            setSearch('');
            suppressSearchRef.current = true;
            requestAnimationFrame(() => {
              if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
              }
            });
          }}
          withCheckIcon={false}
          rightSection={
            infoOrderDetail.order_preset_id || search ? (
              <Box
                component='button'
                type='button'
                onPointerDown={(e: React.PointerEvent) => {
                  handleClearTemplate(e);
                }}
                onTouchStart={(e: React.TouchEvent) => {
                  e.stopPropagation();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                }}>
                <IconCloseGray />
              </Box>
            ) : isFetchingTemplates && hasNextPage ? (
              <Loader size='xs' color='#002CFB' />
            ) : (
              <ArrowSvg />
            )
          }
          rightSectionPointerEvents='auto'
          allowDeselect={false}
          comboboxProps={{
            position: 'bottom',
            middlewares: { flip: false, shift: false },
            offset: 4,
          }}
        />
      ) : (
        <div className={styles.templates}>
          {selectedTemplate ? (
            <div
              className={`${styles.template} ${styles.template_active}`}
              onClick={() => handleClearTemplate()}>
              {selectedTemplate.name}{' '}
              <IconClose
                style={{ cursor: 'pointer', flexShrink: 0 }}
                onPointerDown={(e: React.PointerEvent) => {
                  handleClearTemplate(e);
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
              />
            </div>
          ) : (
            <>
              {firstFiveTemplates.map((template) => (
                <div
                  key={template.id}
                  className={styles.template}
                  onClick={() => handleSelectTemplate(template.id.toString())}>
                  {template.name}
                </div>
              ))}
            </>
          )}

          <div
            style={
              !infoOrderDetail.order_preset_id && remainingTemplates.length > 0
                ? { display: 'block' }
                : { display: 'none' }
            }>
            <Popover
              opened={popoverOpened}
              onChange={(opened) => {
                setPopoverOpened(opened);
                if (!opened) {
                  suppressSearchRef.current = true;
                  setSearch('');
                }
              }}
              width={360}
              position={'right-start'}
              withArrow={false}
              withinPortal={false}
              closeOnClickOutside
              zIndex={1000}
              shadow='md'>
              <Popover.Target>
                <div
                  className={styles.template}
                  onClick={() => setPopoverOpened((o) => !o)}>
                  ...
                </div>
              </Popover.Target>

              <Popover.Dropdown
                maw={360}
                p={'0 4px'}
                onClick={(e) => e.stopPropagation()}
                style={{
                  boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
                  border: 'none',
                }}>
                <InputSearch
                  value={search}
                  placeholder='Искать...'
                  onChange={handleSearchChange}
                />

                <div
                  ref={scrollRef}
                  className={styles.template_wrapper}
                  onScroll={handleScroll}>
                  {allTemplates.length > 0 ? (
                    <div style={{ maxHeight: 300 }}>
                      {allTemplates.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => {
                            handleSelectTemplate(item.id.toString());
                            setPopoverOpened(false);
                            setSearch('');
                          }}
                          className={styles.template_option}
                          onMouseDown={(e) => e.preventDefault()}>
                          {item.name}
                        </div>
                      ))}
                      {isFetchingTemplates && hasNextPage && (
                        <div className={styles.loadMoreRow}>
                          <Loader size='xs' color='#002CFB' />
                        </div>
                      )}
                    </div>
                  ) : isFetchingTemplates ? (
                    <div className={styles.loadMoreRow}>
                      <Loader size='xs' color='#002CFB' />
                    </div>
                  ) : (
                    <div className={styles.nothing_found}>Ничего не найдено</div>
                  )}
                </div>
              </Popover.Dropdown>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
};
